import { API_CONFIG } from '../config/api';
import { User, LoginResponse } from '../types';

/**
 * Authentication service stub.
 * Provides methods for user authentication, token management, and session handling.
 */

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

class AuthService {
    /**
     * Retrieve the stored user from localStorage.
     * Returns the parsed User object or null if not found.
     */
    getStoredUser(): User | null {
        try {
            const userStr = localStorage.getItem(USER_KEY);
            if (userStr) {
                return JSON.parse(userStr) as User;
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * Check whether the user is currently authenticated
     * by verifying the presence of an access token.
     */
    isAuthenticated(): boolean {
        return !!localStorage.getItem(TOKEN_KEY);
    }

    /**
     * Retrieve the stored access token.
     */
    getAccessToken(): string | null {
        return localStorage.getItem(TOKEN_KEY);
    }

    /**
     * Perform an admin login with email, password, and optional captcha token.
     * Returns a LoginResponse with user data and tokens on success.
     */
    async adminLogin(
        email: string,
        password: string,
        captchaToken?: string
    ): Promise<LoginResponse> {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN_LOGIN}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, captchaToken }),
        });

        const data: LoginResponse = await response.json();

        if (data.success && data.data) {
            localStorage.setItem(TOKEN_KEY, data.data.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
            localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
        }

        return data;
    }

    /**
     * Login using a one-time token/code (e.g. magic link or invitation code).
     * Returns a LoginResponse with user data and tokens on success.
     */
    async loginWithToken(code: string): Promise<LoginResponse> {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN_WITH_TOKEN}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
        });

        const data: LoginResponse = await response.json();

        if (data.success && data.data) {
            localStorage.setItem(TOKEN_KEY, data.data.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, data.data.refreshToken);
            localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
        }

        return data;
    }

    /**
     * Log out the current user, clearing all stored tokens and user data.
     */
    async logout(): Promise<void> {
        try {
            const token = this.getAccessToken();
            if (token) {
                await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGOUT}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });
            }
        } finally {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
        }
    }
}

export const authService = new AuthService();
